import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchCommunicationPreferences } from "api/patch-communication-preferences";
import type { CommunicationPreferencesValues } from "components/forms/communications-section/communications-section.component";
import {
  MUTATION_KEYS,
  QUERY_KEYS,
  PREFERENCE_ID_MAP,
} from "common/constants/api";
import { PatchCommunicationPreferenceItem } from "types/types";
import { Options } from "./use-update-user-profile.hook";

export const transformCommunicationPreferences = (
  values: Partial<CommunicationPreferencesValues>
) => {
  const preferences: PatchCommunicationPreferenceItem[] = [];

  if (values.emailNotification !== undefined) {
    preferences.push({
      id: PREFERENCE_ID_MAP.email,
      options: [{ name: "optIn", value: String(values.emailNotification) }],
    });
  }

  if (values.textNotification !== undefined) {
    const textOptions = [
      { name: "optIn", value: String(values.textNotification) },
    ];
    if (values.mobilePhoneNumber) {
      textOptions.push({
        name: "phoneNumber",
        value: values.mobilePhoneNumber,
      });
    }
    preferences.push({
      id: PREFERENCE_ID_MAP.text,
      options: textOptions,
    });
  }

  if (values.paperReports !== undefined) {
    preferences.push({
      id: PREFERENCE_ID_MAP.paperReport,
      options: [{ name: "optIn", value: String(values.paperReports) }],
    });
  }

  return {
    data: {
      id: "communication-preferences",
      type: "communication-preference-data",
      attributes: {
        preferences,
      },
    },
  };
};

export const useUpdateCommunicationPreferences = (options?: Options) => {
  const queryClient = useQueryClient();
  const { onSuccess, onError } = options || {};

  const { mutateAsync: updateCommunicationPreferences, isPending } =
    useMutation({
      mutationKey: [MUTATION_KEYS.COMMUNICATION_PREFERENCES.PATCH],
      mutationFn: (variables: Partial<CommunicationPreferencesValues>) =>
        patchCommunicationPreferences(
          transformCommunicationPreferences(variables)
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.COMMUNICATION_PREFERENCES],
        });
        if (onSuccess) onSuccess();
      },
      onError: (error: Error) => {
        if (onError) onError(error);
      },
    });

  return { updateCommunicationPreferences, isLoading: isPending };
};
